import { Business, Close, Refresh } from "@mui/icons-material"
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { SharedAccessPatternSaEnum } from "shared/sharedAccessPatternsData"
import { Tenant } from "shared/tenant"
import { TenantThingTypeMrn } from "shared/tenantData"
import { useAppSelector } from "src/app/hooks"
import { useGetThingsQuery } from "src/features/things/thingsApi"
import { selectSaList } from "src/features/things/thingsSlice"
import { useSearchParamsState } from "src/hooks/useSearchParamsState"
import ObjectDisplayTable from "./ObjectDisplayTable"
import SaLayout from "./SaLayout"
import SaReservationsByTenant from "./SaReservationsByTenant"
import SaTenantList from "./SaTenantList"

export const SaReservationsPage: FC = () => {
  const { t } = useTranslation()

  const qTenants = useGetThingsQuery({
    params: {
      "ap.name": SharedAccessPatternSaEnum.SaGetTenants,
    },
  })

  const tenants = useAppSelector((state) => selectSaList<Tenant>(state)).filter(
    (t) => t.thingTypeMrn === TenantThingTypeMrn,
  )

  const refresh = useCallback(() => {
    qTenants.refetch()
  }, [qTenants])

  const isLoading = useMemo(
    () => qTenants.isLoading || qTenants.isFetching,
    [qTenants],
  )

  // const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>()
  const [selectedTenantId, setSelectedTenantId] = useSearchParamsState(
    "selectedTenantId",
    "",
  )

  const selectedT = useMemo(
    () => tenants.find((t) => t.id === selectedTenantId),
    [tenants, selectedTenantId],
  )

  const [openPreviewSelectedTenant, setOpenPreviewSelectedTenant] =
    useState<boolean>(false)

  return (
    <SaLayout selectedMenuItemLabelKey="reservations">
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          width: "100%",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "primary.main",
              }}
            >
              {t("adminApp")}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                mt: 0.5,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: "text.primary",
                lineHeight: 1.15,
              }}
            >
              {t("reservations")}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {isLoading ? (
              <CircularProgress size={22} />
            ) : (
              <IconButton
                onClick={refresh}
                size="small"
                sx={{
                  bgcolor: "rgba(35, 66, 133, 0.06)",
                  color: "primary.main",
                  "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Stack>

        {/* Two-column layout: tenant picker + reservations panel */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-start">
          <Box
            sx={{
              bgcolor: "white",
              border: "1px solid",
              borderColor: "rgba(0, 0, 0, 0.08)",
              borderRadius: 2,
              boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
              p: 1.5,
              width: { xs: "100%", md: 320 },
              flexShrink: 0,
              maxHeight: { md: "calc(100vh - 220px)" },
              overflow: "auto",
            }}
          >
            <SaTenantList
              tenants={tenants}
              isLoading={isLoading}
              selectedTenantId={selectedTenantId}
              onSelectTenant={(tenant) => setSelectedTenantId(tenant.id)}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            {!selectedT?.id && (
              <Box
                sx={{
                  bgcolor: "white",
                  border: "1px dashed",
                  borderColor: "rgba(35, 66, 133, 0.2)",
                  borderRadius: 2,
                  p: 6,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 320,
                }}
              >
                <Stack direction="column" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      backgroundImage:
                        "linear-gradient(135deg, rgba(35, 66, 133, 0.12), rgba(148, 210, 194, 0.14))",
                      color: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Business fontSize="medium" />
                  </Box>
                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.95rem",
                      fontWeight: 500,
                    }}
                  >
                    {t("selectTenant")}
                  </Typography>
                </Stack>
              </Box>
            )}
            {selectedT?.id && (
              <Box
                sx={{
                  bgcolor: "white",
                  border: "1px solid",
                  borderColor: "rgba(0, 0, 0, 0.08)",
                  borderRadius: 2,
                  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
                  p: { xs: 2, md: 2.5 },
                }}
              >
                <SaReservationsByTenant tenant={selectedT} />
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
      <Drawer data-ui="UI-SL2W-215"
        anchor="right"
        open={!!openPreviewSelectedTenant}
        onClose={() => setOpenPreviewSelectedTenant(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "80vw",
            maxWidth: "1500px",
            maxHeight: "calc(100vh - 64px)",
            height: "calc(100vh - 64px)",
            paddingTop: "64px",
          },
        }}
      >
        <Stack direction="column" spacing={1} p={2}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  color: "primary.main",
                }}
                divider={<Divider orientation="vertical" flexItem />}
              >
                <Typography variant="body1" fontWeight="bold">
                  {selectedT?.name}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedT?.id}
                </Typography>
              </Stack>
            </Stack>
            <IconButton onClick={() => setOpenPreviewSelectedTenant(false)}>
              <Close />
            </IconButton>
          </Stack>
          <Divider />

          <ObjectDisplayTable data={selectedT} />
        </Stack>
      </Drawer>
    </SaLayout>
  )
}

export default SaReservationsPage
